import {
  ChangeDetectorRef,
  Inject,
  OnDestroy,
  Optional,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { DebugTranslationsDI } from '../models/translation-core.model';
import { TranslationCoreService } from '../services/translation-core.service';

@Pipe({
  name: 'translate',
  pure: false,
  standalone: false,
})
export class TranslateCorePipe implements PipeTransform, OnDestroy {
  private readonly translationKeySubject = new BehaviorSubject('');
  private readonly translationKey$ = this.translationKeySubject
    .asObservable()
    .pipe(distinctUntilChanged());
  private readonly translationParamsSubject = new BehaviorSubject<
    (string | number | string[])[]
  >([]);
  private readonly translationParams$ = this.translationParamsSubject
    .asObservable()
    .pipe(distinctUntilChanged(this.paramsEqual));

  private lastKey = '';
  private lastParams: (string | number | string[])[] = [];
  private readonly translatedValueSubject = new BehaviorSubject<string>('');
  private readonly translatedValue$ = this.translatedValueSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private destroy$ = new Subject<void>();

  private translatedValue = '';

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private translationCoreService: TranslationCoreService,
    @Optional() @Inject(DebugTranslationsDI) private debugTranslation: boolean
  ) {
    combineLatest([this.translationKey$, this.translationParams$])
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(0),
        switchMap(([key, params]) => {
          if (key === '') {
            return EMPTY;
          }

          return this.translationCoreService.getTranslation(key, {
            params,
          });
        }),
        tap(value => {
          this.updateTranslateValue(value);
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.translatedValue$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(value => {
        if (value !== this.translatedValue) {
          this.translatedValue = value;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  transform(
    key: string | number | undefined,
    params?: (string | number | string[])[],
    fallback?: string
  ): string {
    if (typeof key === 'number' || !key || !key.length) {
      return String(key);
    }
    const p = params ?? [];
    if (this.lastKey === key && this.paramsEqual(this.lastParams, p)) {
      return this.translatedValue;
    }
    this.lastKey = key;
    this.lastParams = p;

    let noTranslation = '';
    if (this.debugTranslation || fallback) {
      noTranslation = fallback || key;
    }
    this.translationKeySubject.next(key);
    this.translationParamsSubject.next(p);
    this.translatedValueSubject.next(noTranslation);

    const instantValue = this.translationCoreService.instant(key, params);

    if (instantValue !== key || !instantValue) {
      this.translatedValue = instantValue;
    }

    return this.translatedValue;
  }

  private paramsEqual(
    a: (string | number | string[])[],
    b: (string | number | string[])[]
  ): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  private updateTranslateValue(value?: string | null): void {
    const key = this.translationKeySubject.getValue();
    const isNoTranslation = value == null || value === '' || value === key;

    if (isNoTranslation && this.debugTranslation) {
      console.warn(`[Translations] No translation for - '${key}'`);
    }
    this.translatedValueSubject.next(
      isNoTranslation ? (this.debugTranslation ? key : '') : value
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
